/**
 * Firebase Auth error-code → i18n message-key mapping for the agent-pwa
 * sign-in + password-reset surfaces.
 *
 * Kept as PURE functions (no Firebase import, no React) so they unit-test
 * without an emulator and so the sign-in component stays declarative. Callers
 * resolve the returned key through `useI18n().t(...)`, which honours rule #1
 * (no JSX string literals) and [[feedback-custom-crafted-ui-text]] — every
 * failure state gets a specific, human line, never a raw Firebase code and
 * never a dead "Error" ([[feedback-no-dead-ends]]).
 *
 * This is a deliberate small copy of the dashboard's `login-errors.ts` (branch
 * loop/dashboard-login). The mapping LOGIC is identical; only the returned i18n
 * keys differ by namespace (`agent.login.*` vs the dashboard's `auth.login.*`),
 * because the agent copy has its own voice. Per [[feedback-unified-packages-ui]]
 * the honest home for this is `@kilat/shared` parameterised by key namespace —
 * FOLLOW-UP once loop/dashboard-login merges (extracting now would edit that
 * unmerged branch and collide its `auth.login.*` i18n keys with a shared home).
 */

/**
 * Extract a Firebase `auth/*` error code from an unknown thrown value.
 * `FirebaseError` carries a string `code`; anything else yields `''` (→ the
 * generic bucket). No `any` / non-null assertions — a narrowing guard instead.
 */
export function firebaseAuthErrorCode(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { readonly code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * Map a sign-in failure to a human i18n key under `agent.login.*`.
 *
 * `invalid-credential` / `wrong-password` / `user-not-found` all collapse to
 * the SAME "email or password is incorrect" line — telling the agent WHICH of
 * the two was wrong would leak whether an account exists (account-enumeration).
 */
export function loginErrorKey(err: unknown): string {
  switch (firebaseAuthErrorCode(err)) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'agent.login.errInvalidCredential';
    case 'auth/invalid-email':
      return 'agent.login.errInvalidEmail';
    case 'auth/too-many-requests':
      return 'agent.login.errTooManyRequests';
    case 'auth/user-disabled':
      return 'agent.login.errUserDisabled';
    case 'auth/network-request-failed':
      return 'agent.login.errNetwork';
    default:
      return 'agent.login.errGeneric';
  }
}

/**
 * Map a "Continue with Google" failure to a human i18n key under
 * `agent.login.*`. Popup-cancel is a benign user action (return `null` → show
 * nothing); config errors (`operation-not-allowed` = provider not enabled,
 * `unauthorized-domain` = origin not allow-listed) get a distinct line so the
 * OWNER can tell a console-setup gap from a real failure.
 *
 * NOTE: `account-exists-with-different-credential` is NOT handled here — the
 * caller intercepts it BEFORE this to start the account-link flow.
 */
export function googleErrorKey(err: unknown): string | null {
  switch (firebaseAuthErrorCode(err)) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return null; // the agent dismissed the popup — not an error to shout about
    case 'auth/popup-blocked':
      return 'agent.login.errPopupBlocked';
    case 'auth/operation-not-allowed':
    case 'auth/unauthorized-domain':
      return 'agent.login.errGoogleConfig';
    case 'auth/too-many-requests':
      return 'agent.login.errTooManyRequests';
    case 'auth/network-request-failed':
      return 'agent.login.errNetwork';
    default:
      return 'agent.login.errGoogleGeneric';
  }
}

/**
 * Map a password-reset failure to an i18n key — or `null` when the failure
 * MUST be treated as success to avoid leaking whether an email is registered
 * (`auth/user-not-found`). Callers show the neutral "check your inbox"
 * confirmation on `null`, so an attacker can't enumerate accounts through the
 * reset form.
 */
export function resetErrorKey(err: unknown): string | null {
  switch (firebaseAuthErrorCode(err)) {
    case 'auth/user-not-found':
      return null; // do NOT leak account existence — show the confirmation
    case 'auth/invalid-email':
      return 'agent.login.resetErrInvalidEmail';
    case 'auth/too-many-requests':
      return 'agent.login.resetErrTooManyRequests';
    case 'auth/network-request-failed':
      return 'agent.login.resetErrNetwork';
    default:
      return 'agent.login.resetErrGeneric';
  }
}
